<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import type { Product } from '@/entities/product';
  import type { SearchCriteria } from '@/entities/searchCriteria';
  import { SERP_BANNERS, SERP_BANNERS_GRID, SERP_BANNERS_PRIORITY, SERP_STATIC_BANNERS } from '../config/banners';
  import { getSerpBannerAt } from '../lib/getSerpBannerAt';
  import { SerpBannerId, type SerpBanner } from '../model/types';

  interface SerpBannersProps {
    product: Product;
    searchCriteria: SearchCriteria;
    cardPosition: number;
    totalCards: number;
    isAlternativeSearch?: boolean;
    isShortSerp?: boolean;
  }

  const props = withDefaults(defineProps<SerpBannersProps>(), {
    isAlternativeSearch: false,
    isShortSerp: false,
  });

  interface SerpBannersEmits {
    (e: 'emirates'): void;
  }

  const emit = defineEmits<SerpBannersEmits>();

  const banner = computed<SerpBanner | null>(() =>
    getSerpBannerAt(
      {
        priority: SERP_BANNERS_PRIORITY,
        staticBanners: SERP_STATIC_BANNERS,
        banners: SERP_BANNERS,
        grid: SERP_BANNERS_GRID,
      },
      {
        searchCriteria: props.searchCriteria,
        totalCards: props.totalCards,
        isAlternativeSearch: props.isAlternativeSearch,
        isShortSerp: props.isShortSerp,
      },
      props.cardPosition,
    ),
  );

  const onSelect = (): void => {
    if (banner.value?.id === SerpBannerId.Emirates) emit('emirates');
  };

  onMounted(() => {
    Object.values(SERP_BANNERS).forEach((serpBanner) => serpBanner.preload?.());
  });
</script>

<template>
  <div
    v-if="banner"
    class="serp-banners"
    :data-testid="`serp-banner-${banner.id}`"
  >
    <component
      :is="banner.component"
      :product="product"
      :search-criteria="searchCriteria"
      :card-position="cardPosition"
      @select="onSelect"
    />
  </div>
</template>

<style scoped lang="scss">
  .serp-banners {
    box-sizing: border-box;
    padding-top: var(--spacing-xs);
    width: 100%;
  }
</style>
